import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";
import {
  adminTable,
  adminTableDataCell,
} from "../common/AdminTableStyles";
import { STATES } from "../../../util/constant";

const breakpoints = {
  tablet: 768,
  phone:  480,
};


const Container = styled.div`
  padding: 1.5em;
`;

const SectionHeader = styled.h1`
  margin-top: 1em;
   color: ${({ colors }) => colors.labelText};
  margin: 0 0 1em;
  font-size: 1.75em;

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 1.25em;
  }
`;

const FormWrapper = styled.div`
  background: #f5f5f5;
//   padding: 1em;
  border-radius: 4px;
`;

const FormTable = styled.table`
  ${adminTable};
  margin-bottom: 1em;

  td {
    ${adminTableDataCell};
    vertical-align: top;
    padding: 0.5em 0;
  }
`;

const InnerTable = styled.table`
  ${adminTable};
  width: 100%;
  background: #f5f5f5;

  td {
    ${adminTableDataCell};
    padding: 1em 0.5em;
    border-bottom: 1px solid #ddd;
  }
`;

const TdLabel = styled.td`
  width: 30%;
  font-weight: bold;
  padding: 1em 1em;
`;

const Input = styled.input`
  padding: 0.4em;
  font-size: 1em;
  border: 2px solid #000;
  border-radius: 4px;
  width: ${({ full }) => (full ? "90%" : "60%")};
`;

const Select = styled.select`
  padding: 0.4em;
  font-size: 1em;
  border: 2px solid #000;
  border-radius: 4px;
`;

const CheckboxLabel = styled.label`
  margin-left: 0.5em;
`;

const SubmitRow = styled.div`
  margin-top: 1em;
  input {
    padding: 0.6em 1.2em;
    font-size: 1em;
    border: none;
    border-radius: 4px;
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    cursor: pointer;
    &:hover {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }
  }
`;

export default function RenewSubscriptionContent({ colors }) {
  const selected = useSelector((s) => s.auth.selected_subscriptions) || {};
  console.log(selected)

  // split stored product_code into base + blue flag
  const [baseProduct, brFlag] = (selected.product_code || "").split("-br");
  const isBR = brFlag === "br";

  // fetched product codes
  const [productCodes, setProductCodes] = useState([]);

  useEffect(() => {
    API.getProductCodes({})
      .then((resp) => {
        // assume resp.result is an array of { product_code, product_desc }
        setProductCodes(resp.result || []);
      })
      .catch((err) => console.error("Failed to load product codes:", err));
  }, []);

  const [form, setForm] = useState({
    org_name: selected.org_name || "",
    org_type: selected.org_type || "district",
    street:   selected.org_street || "",
    city:     selected.org_city   || "",
    state:    selected.org_state  || "",
    zip:      selected.org_zip    || "",
    schools:  selected.number_of_schools || "",
    students: selected.number_of_users   || "",
    first_name:   (selected.contact_name || "").split(" ")[0] || "",
    last_name:    (selected.contact_name || "").split(" ").slice(1).join(" ") || "",
    contact_email:  selected.contact_email   || "",
    contact_phone:  selected.contact_phone   || "",
    product:        baseProduct  || "select",
    includeBlueRibbon: isBR,
    educators:       selected.number_of_users || "",
  });

  const [saving, setSaving] = useState(false);
  const [modal , setModal]  = useState({ show:false, title:"", message:"", status: "Success" });

  const isDistrict = form.org_type === "district";
  const showEdu    = form.product === "pdp";
  const blueProducts = ["erate","gold","aup"];
  const showBlue  = blueProducts.includes(form.product) && !showEdu;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({
      ...f,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);

    const productCode = showBlue && form.includeBlueRibbon
      ? `${form.product}-br`
      : form.product;

    API.submitRenewalQuote({
      customer_id: selected.customer_id,
      org_name:    form.org_name,
      org_type:    form.org_type,
      street:      form.street,
      city:        form.city,
      state:       form.state,
      zip:         form.zip,
      ...(isDistrict ? { schools: form.schools } : {}),
      students:         form.students,
      first_name:       form.first_name,
      last_name:        form.last_name,
      contact_email:    form.contact_email,
      contact_phone:    form.contact_phone,
      product:          productCode,
      ...(showEdu ? { educators: form.educators } : {}),
    })
      .then(resp => {
        setModal({
          show: true,
          title: resp?.status === "Failed" ? "Error" : "Quote Sent",
          message: resp.message || (resp?.status === "Failed" ? "Quote failed to send." : undefined),
          status: resp?.status === "Failed" ? "Failed" : "Success"
        });
      })
      .catch(err => {
        setModal({ show:true, title:"Error", message: err.message, status: "Failed" });
      })
      .finally(() => setSaving(false));
  };

  const closeModal = () => {
    setModal((m) => ({ ...m, show: false, status: "Success" }));
  };

  return (
    <Container>
      <SectionHeader colors={colors}>Renew Subscription Request</SectionHeader>
      <p>Please provide as much information as you can.</p>

      <FormWrapper>
        <form onSubmit={handleSubmit}>
          <input type="hidden" name="customer_id" value={selected.customer_id}/>

          <FormTable>
            <tbody>
              <tr><TdLabel colSpan="2"><h2>Customer Information</h2></TdLabel></tr>
              <tr>
                <td colSpan="2">
                  <InnerTable>
                    <tbody>
                      <tr>
                        <TdLabel>Name of Organization*</TdLabel>
                        <td colSpan="2">
                          <Input
                            colors={colors}
                            full
                            name="org_name"
                            value={form.org_name}
                            onChange={handleChange}
                            required
                          />
                        </td>
                      </tr>
                      <tr>
                        <TdLabel>Type of Organization*</TdLabel>
                        <td colSpan="2">
                          <input
                            type="radio"
                            name="org_type"
                            value="school"
                            checked={form.org_type === "school"}
                            onChange={handleChange}
                          />{" "}
                          <CheckboxLabel>School</CheckboxLabel>
                          <input
                            style={{ marginLeft:"1em" }}
                            type="radio"
                            name="org_type"
                            value="district"
                            checked={form.org_type === "district"}
                            onChange={handleChange}
                          />{" "}
                          <CheckboxLabel>District</CheckboxLabel>
                        </td>
                      </tr>
                      <tr>
                        <TdLabel>Street</TdLabel>
                        <td colSpan="2">
                          <Input
                            colors={colors}
                            full
                            name="street"
                            value={form.street}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <TdLabel>City</TdLabel>
                        <td colSpan="2">
                          <Input
                            colors={colors}
                            full
                            name="city"
                            value={form.city}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <TdLabel>State</TdLabel>
                        <td colSpan="2">
                          <Select
                            colors={colors}
                            name="state"
                            value={form.state}
                            onChange={handleChange}
                            required
                          >
                            <option value="">— select —</option>
                            {Object.entries(STATES).map(([label, code]) => (
                              <option key={code} value={code}>
                                {label}
                              </option>
                            ))}
                          </Select>
                        </td>
                      </tr>
                      <tr>
                        <TdLabel>Zip</TdLabel>
                        <td>
                          <Input
                            colors={colors}
                            name="zip"
                            value={form.zip}
                            onChange={handleChange}
                          />
                        </td>
                        <td>&nbsp;</td>
                      </tr>
                      {isDistrict && (
                        <tr>
                          <TdLabel>Number of Schools in District*</TdLabel>
                          <td>
                            <Input
                              colors={colors}
                              name="schools"
                              value={form.schools}
                              onChange={handleChange}
                              required
                            />
                          </td>
                          <td>Schools</td>
                        </tr>
                      )}
                      <tr>
                        <TdLabel>Number of Students in District*</TdLabel>
                        <td>
                          <Input
                            colors={colors}
                            name="students"
                            value={form.students}
                            onChange={handleChange}
                            required
                          />
                        </td>
                        <td>&nbsp;</td>
                      </tr>
                    </tbody>
                  </InnerTable>
                </td>
              </tr>

              <tr><TdLabel colSpan="2"><h2>Contact Information</h2></TdLabel></tr>
              <tr>
                <td colSpan="2">
                  <InnerTable>
                    <tbody>
                      <tr>
                        <TdLabel>First Name*</TdLabel>
                        <td>
                          <Input
                            colors={colors}
                            name="first_name"
                            value={form.first_name}
                            onChange={handleChange}
                            required
                          />
                        </td>
                        <td>&nbsp;</td>
                      </tr>
                      <tr className="altrow">
                        <TdLabel>Last Name*</TdLabel>
                        <td>
                          <Input
                            colors={colors}
                            name="last_name"
                            value={form.last_name}
                            onChange={handleChange}
                            required
                          />
                        </td>
                        <td>&nbsp;</td>
                      </tr>
                      <tr>
                        <TdLabel>E-mail*</TdLabel>
                        <td>
                          <Input
                            colors={colors}
                            name="contact_email"
                            type="email"
                            value={form.contact_email}
                            onChange={handleChange}
                            required
                          />
                        </td>
                        <td>&nbsp;</td>
                      </tr>
                      <tr className="altrow">
                        <TdLabel>Phone</TdLabel>
                        <td>
                          <Input
                            colors={colors}
                            name="contact_phone"
                            value={form.contact_phone}
                            onChange={handleChange}
                          />
                        </td>
                        <td>&nbsp;</td>
                      </tr>
                    </tbody>
                  </InnerTable>
                </td>
              </tr>

              <tr><TdLabel colSpan="2"><h2>Product Information</h2></TdLabel></tr>
              <tr>
                <td colSpan="2">
                  <InnerTable>
                    <tbody>
                      <tr>
                        <TdLabel>Select product type*</TdLabel>
                        <td>
                          <Select
                            colors={colors}
                            name="product"
                            value={form.product}
                            onChange={handleChange}
                            required
                          >
                            <option value="select">Select</option>
                            {productCodes.map((p) => (
                              <option
                                key={p.product_code}
                                value={p.product_code}
                              >
                                {p.product_desc}
                              </option>
                            ))}
                          </Select>
                          {/* {showBlue && (
                            <CheckboxLabel>
                              <input
                                type="checkbox"
                                name="includeBlueRibbon"
                                checked={form.includeBlueRibbon}
                                onChange={handleChange}
                              />{" "}
                              Add Blue Ribbon Service
                            </CheckboxLabel>
                          )} */}
                        </td>
                        <td>&nbsp;</td>
                      </tr>
                      {showEdu && (
                        <tr>
                          <TdLabel>Number of Educators</TdLabel>
                          <td>
                            <Input
                              colors={colors}
                              name="educators"
                              value={form.educators}
                              onChange={handleChange}
                              required
                            />
                          </td>
                          <td>&nbsp;</td>
                        </tr>
                      )}
                    </tbody>
                  </InnerTable>
                </td>
              </tr>
            </tbody>
          </FormTable>

          <SubmitRow colors={colors}>
            <input
              type="submit"
              value={saving ? "Sending…" : "Send Quote"}
              disabled={saving}
            />
          </SubmitRow>
        </form>
      </FormWrapper>

      <ConfirmationModal
        show={modal.show}
        title={modal.title}
        message={modal.message}
        onCancel={closeModal}
        onConfirm={closeModal}
        status={modal.status}
        single
      />
    </Container>
  );
}
